import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideChevronDown } from '@ng-icons/lucide';
import { LUMIR_PRODUCTS, type LumirProduct } from '@environment';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { HlmLogo } from '@lite-workspace/shared/helm-ui/logo';
import { PHOTOBOOK_MENU_ENABLED } from './photobook-menu-enabled.token';
import { isProductOffered } from './product-offered';
import { PRODUCT_MENU_ENABLED } from './product-menu-enabled.token';

/**
 * The shell's logo slot. With the product menu on ({@link PRODUCT_MENU_ENABLED}) the wordmark IS the
 * product switcher: clicking it opens the list of LUMIR surfaces, the current one marked instead of
 * linked. With the menu off the wordmark is a link to the shell's main page (`homeLink`), or a static
 * mark when the shell has none — exactly what prod shows today. Reads {@link LUMIR_PRODUCTS} itself,
 * so a new surface joins the menu by landing one row there — no shell touches the list.
 */
@Component({
  selector: 'hlm-product-switcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex min-w-0 items-center' },
  imports: [NgIcon, HlmBadge, HlmButton, HlmLogo, HlmDropdownMenuImports, RouterLink],
  providers: [provideIcons({ lucideCheck, lucideChevronDown })],
  template: `
    @if (menuEnabled) {
      <button
        hlmBtn
        type="button"
        variant="ghost"
        [hlmDropdownMenuTrigger]="productMenu"
        align="start"
        side="bottom"
        aria-label="Переключить продукт"
        class="h-7.5 gap-1.5 border border-transparent px-2 hover:border-line2"
      >
        <hlm-logo [word]="logoWord()" [hideWordWhenNarrow]="hideWordWhenNarrow()" />
        @if (currentProduct()?.beta) {
          <!-- Метка стоит при имени продукта: имя прячется на узкой шапке — прячется и она, иначе шапка читается «LUMIR beta». -->
          <span [class]="hideWordWhenNarrow() ? 'hidden compact:inline-flex' : 'inline-flex'">
            <span hlmBadge variant="acc-outline">beta</span>
          </span>
        }
        <ng-icon name="lucideChevronDown" class="text-sm text-c3" />
      </button>
    } @else if (homeLink(); as link) {
      <a
        [routerLink]="link"
        [attr.aria-label]="'LUMIR ' + logoWord()"
        class="inline-flex h-7.5 cursor-pointer items-center border border-transparent px-2"
      >
        <hlm-logo [word]="logoWord()" [hideWordWhenNarrow]="hideWordWhenNarrow()" />
      </a>
    } @else {
      <span class="inline-flex h-7.5 items-center border border-transparent px-2">
        <hlm-logo [word]="logoWord()" [hideWordWhenNarrow]="hideWordWhenNarrow()" />
      </span>
    }

    <ng-template #productMenu>
      <div hlmDropdownMenu class="min-w-65 gap-0">
        @for (product of products(); track product.id) {
          @if (!$first) {
            <span hlmDropdownMenuSeparator class="mx-3 my-0"></span>
          }
          @if (product.id === currentProductId()) {
            <span
              hlmDropdownMenuItem
              size="lg"
              class="after:absolute after:top-2.5 after:bottom-2.5 after:left-1 after:w-0.5 after:rounded-full after:bg-acc after:content-['']"
            >
              <span class="min-w-0 grow">
                <span class="flex items-center gap-2">
                  <b class="font-semibold text-acc">{{ product.label }}</b>
                  @if (product.beta) {
                    <span hlmBadge variant="acc-outline">beta</span>
                  }
                </span>
                <em class="mt-0.5 block text-xs not-italic text-c2">{{ product.role }}</em>
              </span>
              <ng-icon name="lucideCheck" class="shrink-0 text-acc" />
            </span>
          } @else {
            <a
              hlmDropdownMenuItem
              size="lg"
              [href]="product.url"
              class="hover:bg-panel2 data-kb-focus:bg-panel2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-acc focus-visible:ring-inset"
            >
              <span class="min-w-0 grow">
                <span class="flex items-center gap-2">
                  <b class="font-normal text-ink">{{ product.label }}</b>
                  @if (product.beta) {
                    <span hlmBadge variant="acc-outline">beta</span>
                  }
                </span>
                <em class="mt-0.5 block text-xs not-italic text-c2">{{ product.role }}</em>
              </span>
            </a>
          }
        }
      </div>
    </ng-template>
  `,
})
export class HlmProductSwitcher {
  /** This surface's own identity — the row it matches is marked, never linked. */
  readonly currentProductId = input.required<LumirProduct['id']>();
  readonly hideWordWhenNarrow = input(true);
  /** Main page of the shell the wordmark leads to while the menu is off; null keeps it static. */
  readonly homeLink = input<string | null>(null);

  protected readonly menuEnabled = inject(PRODUCT_MENU_ENABLED);
  private readonly photobookRowEnabled = inject(PHOTOBOOK_MENU_ENABLED);

  /**
   * Строка продукта живёт в меню, когда продукт предлагается ({@link isProductOffered}: хост поднят и
   * продукт готов). Текущий продукт остаётся всегда, мимо этого правила: он помечается, а не линкуется,
   * поэтому собственная шапка каждой поверхности цела.
   */
  protected readonly products = computed<readonly LumirProduct[]>(() =>
    LUMIR_PRODUCTS.filter(
      product => product.id === this.currentProductId() || isProductOffered(product, this.photobookRowEnabled),
    ),
  );

  /**
   * Второе слово логотипа — имя текущего продукта из того же списка, что рисует меню: шелл ничего
   * не передаёт, поэтому вордмарк и строка меню не могут разойтись. Регистр даёт CSS, так что здесь
   * ровно та же строка, что в меню.
   */
  protected readonly currentProduct = computed(() => LUMIR_PRODUCTS.find(product => product.id === this.currentProductId()));

  protected readonly logoWord = computed<string>(() => this.currentProduct()?.label ?? 'PHOTO');
}
